import { publishedAt, modifiedAt, postDateByline } from "@/data/postDates";
import { postFaqs } from "@/data/postFaqs";
import { articleTableHtml } from "@/data/articleTables";
import { createFileRoute, Link } from "@tanstack/react-router";
import { BookFitCall } from "@/components/site/BookFitCall";
import { articleSchema, canonicalLinks, faqPageSchema, jsonLd, ogImage, ogUrl, breadcrumbSchema, webPageSchema, absoluteUrl } from "@/lib/seo";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { PhotoBand } from "@/components/site/PhotoBand";
import { ToolCallout } from "@/components/site/tools/ToolCallout";
import founderDeskAfterHoursImg from "@/assets/photos/founder-desk-after-hours.webp";

const SLUG = "fractional-coo-cost-australia";
const META_TITLE = "Fractional COO Cost in Australia (2026)";
const TITLE = "How much does a fractional COO cost in Australia?";
const DESCRIPTION =
  "Australian guides put a fractional COO at $6,000 to $25,000 a month. Ajility publishes its own: retainers under $15M start from $3,900 per month, plus GST.";
const CATEGORY = "Pricing";
const READ = "6 min read";

const faqLd = postFaqs[SLUG];

const faqs = [
  {
    q: "Are the prices really the prices?",
    a: (
      <>
        Yes, plus GST, published on the <Link to="/packages" className="font-medium text-midnight underline decoration-border underline-offset-4 transition hover:decoration-midnight">packages page</Link>, and they change there first. Where a figure on this page and the packages page ever differ, the packages page is current.
      </>
    ),
  },
  {
    q: "What's the minimum commitment?",
    a: "One quarter. Retainers are committed 90 days at a time and reviewed at each boundary, renew or exit.",
  },
  {
    q: "Why publish prices when no other consultancy does?",
    a: (
      <>
        Because pricing is literally Ajility's specialisation, and a pricing consultant who hides his prices is telling you something. See{" "}
        <Link to="/insights/$slug" params={{ slug: "why-we-publish-our-prices" }} className="font-medium text-midnight underline decoration-border underline-offset-4 transition hover:decoration-midnight">why we publish our prices</Link>{" "}
        for the longer answer.
      </>
    ),
  },
  {
    q: "Is a fractional COO cheaper than a consultant?",
    a: (
      <>
        Different, rather than cheaper. Consultants price by the day and deliver recommendations; a fractional COO prices by the outcome and is accountable for delivery inside your leadership rhythm. See the comparison with{" "}
        <Link to="/insights/$slug" params={{ slug: "fractional-coo-vs-consultant-vs-coach" }} className="font-medium text-midnight underline decoration-border underline-offset-4 transition hover:decoration-midnight">consultants and coaches</Link>{" "}
        for more.
      </>
    ),
  },
  {
    q: "What does the first conversation cost?",
    a: (
      <>
        Nothing. The fit call is free and thirty minutes, and the{" "}
        <Link to="/scan" className="font-medium text-midnight underline decoration-border underline-offset-4 transition hover:decoration-midnight">Operating Scan</Link>{" "}
        before it is free and six minutes.
      </>
    ),
  },
];

export const Route = createFileRoute("/insights/fractional-coo-cost-australia")({
  head: () => ({
    meta: [
      { title: `${META_TITLE} | Ajility` },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: `${META_TITLE} | Ajility` },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { name: "twitter:title", content: `${META_TITLE} | Ajility` },
      { name: "twitter:description", content: DESCRIPTION },
      ...ogImage(`insights-${SLUG}`, META_TITLE),
      ogUrl(`/insights/${SLUG}`),
    ],
    links: canonicalLinks(`/insights/${SLUG}`),
    scripts: [
      jsonLd(breadcrumbSchema([{ name: "Insights", path: "/insights" }, { name: TITLE, path: `/insights/${SLUG}` }])),
      jsonLd(
        webPageSchema({
          path: `/insights/${SLUG}`,
          name: `${META_TITLE} | Ajility`,
          description: DESCRIPTION,
          dateModified: modifiedAt(SLUG),
          mainEntityId: `${absoluteUrl(`/insights/${SLUG}`)}#article`,
          image: `insights-${SLUG}`,
        }),
      ),
      jsonLd(
        articleSchema({
          title: `${META_TITLE} | Ajility`,
          description: DESCRIPTION,
          slug: SLUG,
          datePublished: publishedAt(SLUG),
          dateModified: modifiedAt(SLUG),
          section: CATEGORY,
        }),
      ),
      jsonLd(faqPageSchema(`/insights/${SLUG}`, faqLd, `${absoluteUrl(`/insights/${SLUG}`)}#article`)),
    ],
  }),
  component: FractionalCooCostAustralia,
});

function FractionalCooCostAustralia() {
  return (
    <div className="bg-background">
      {/* Hero */}
      <section className="relative gradient-navy text-frost">
        <div className="grid-lines absolute inset-0 opacity-20" />
        <div className="relative mx-auto max-w-3xl px-5 pb-16 pt-36 sm:px-8 sm:pt-44">
          <Link
            to="/insights"
            className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em] text-frost/70 transition hover:text-frost"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> All operating insights and case studies
          </Link>
          <span className="eyebrow on-dark mt-8 block">{CATEGORY}, {READ}</span>
          <h1 className="mt-4 font-serif text-4xl leading-[1.08] text-frost sm:text-5xl">
            {TITLE}
          </h1>
          <p className="mt-3 text-sm text-frost/70">{postDateByline(SLUG)}</p>
          <p className="mt-6 text-lg leading-relaxed text-frost/80">{DESCRIPTION}</p>
          <div className="mt-10 flex items-center gap-4 border-t border-white/10 pt-6 text-sm text-frost/70">
            <div className="grid h-10 w-10 place-items-center rounded-full bg-frost/10 font-serif text-frost">
              A
            </div>
            <div>
              By <Link to="/about/adam-ayoub" className="font-medium text-frost underline decoration-white/30 underline-offset-4 transition hover:decoration-frost">Adam Ayoub</Link>
              <br />
              <span className="text-frost/80">Fractional COO, Ajility</span>
            </div>
          </div>
        </div>
      </section>


      <PhotoBand src={founderDeskAfterHoursImg} alt="A business owner at a cluttered desk after hours reading a printed proposal under a desk lamp, laptop open beside them." />

      {/* Article */}
      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
        <article className="article-body">
          <p>
            A fractional COO in Australia typically costs between $6,000 and $25,000 a month according to published market guides (Expert360's 2026 hiring guide; SEEK salary data 2025 as cited by Fractionus), with most engagements landing in the $8,000 to $16,000 range. Ajility publishes its actual prices rather than a range. Most composed retainers for businesses under $15M start from $3,900 per month plus GST, depending on cadence and how many parts of the business are actively led, and every figure is on the{" "}
            <Link to="/packages" className="font-medium text-midnight underline decoration-border underline-offset-4 transition hover:decoration-midnight">packages page</Link>.
          </p>
          <p>
            That first paragraph is the whole answer. The rest of this page explains what moves the number, and shows the actual ladder, because a market that mostly answers this question with &quot;book a call&quot; deserves at least one provider who answers it with prices.
          </p>

          <div dangerouslySetInnerHTML={{ __html: articleTableHtml(SLUG) }} />

          <h2>Why the market quotes a range</h2>
          <p>
            Fractional COO pricing in Australia varies on three things: how much of the week you get, how senior the operator is, and whether the engagement is advisory or embedded. Fractionus&apos;s 2026 Australian pricing guide puts light-touch operational oversight around $6,000 to $8,000 a month, hands-on operational leadership at $10,000 to $15,000, and full transformation work at $15,000 to $25,000. Those are fair descriptions of the market. What almost no provider does is tell you where their number sits before you get on a phone.
          </p>

          <h2>The Ajility ladder, with the actual figures</h2>
          <p>
            All figures plus GST, current as at September 2026. The{" "}
            <Link to="/packages" className="font-medium text-midnight underline decoration-border underline-offset-4 transition hover:decoration-midnight">packages page</Link>{" "}
            is always the current version.
          </p>
          <p>
            Finding out what&apos;s wrong comes first, and it&apos;s the cheapest step. The Operating Scan is free, self-serve, about six minutes. The Operating Diagnostic, a desktop review with a Team Survey, a read of your numbers and a working session, is from $3,600, and the Full Diagnostic and 90-Day Roadmap, run on site, is from $6,900 plus GST for one entity.
          </p>
          <p>
            Fixing one thing properly is a fixed price with a defined end. Single install prices range from $3,000 for one process, one fix, to $18,000 for the full Operating System and Governance install, each priced and scoped on the{" "}
            <Link to="/packages" className="font-medium text-midnight underline decoration-border underline-offset-4 transition hover:decoration-midnight">packages page</Link>{" "}
            before you commit.
          </p>
          <p>
            Ongoing leadership is composed, not flat-rated. A retainer is built from a rhythm base, from $3,900 per month fortnightly or from $6,900 per month weekly, plus a service-line fee from $2,100 to $3,000 a month for each part of the business actively led. Most composed retainers for businesses under $15M start from $3,900 per month plus GST, depending on the mix and the size of the business. Engagements commit one quarter at a time.
          </p>

          <figure>
            <svg
              viewBox="-120 0 920 320"
              role="img"
              aria-label="Illustrative annual cost comparison: full-time COO at $300,000 or more a year versus Ajility composed retainer starting from $46,800 a year plus GST"
            >
              <rect width="800" height="320" fill="#ffffff" />
              <text x="180" y="75" textAnchor="end" fontFamily="Montserrat,sans-serif" fontSize="13" fontWeight="700" fill="#1F2A44">
                Full-time COO
              </text>
              <text x="180" y="98" textAnchor="end" fontFamily="Inter,sans-serif" fontSize="12" fill="#7A8194">
                $300K+ a year (salary, super, incentives)
              </text>
              <rect x="200" y="58" width="540" height="48" rx="8" fill="#1F2A44" />
              <text x="720" y="88" textAnchor="end" fontFamily="Inter,sans-serif" fontSize="14" fontWeight="600" fill="#ffffff">
                $300,000+
              </text>

              <text x="180" y="188" textAnchor="end" fontFamily="Montserrat,sans-serif" fontSize="13" fontWeight="700" fill="#1F2A44">
                Ajility composed retainer
              </text>
              <text x="180" y="211" textAnchor="end" fontFamily="Inter,sans-serif" fontSize="12" fill="#7A8194">
                from $47K a year plus GST, no on-costs, quarterly exit
              </text>
              <rect x="200" y="171" width="260" height="48" rx="8" fill="#8a97b5" />
              <text x="440" y="201" textAnchor="end" fontFamily="Inter,sans-serif" fontSize="14" fontWeight="600" fill="#ffffff">
                from $47K plus GST
              </text>
            </svg>
            <figcaption>
              <strong>Illustrative comparison.</strong> The fractional figure is Ajility&apos;s published composed starting price annualised; the full-time figure is the benchmark recruitment guides commonly cite.
            </figcaption>
          </figure>

          <ToolCallout tool="fractional-coo-cost-calculator" articleSlug={SLUG} className="my-10" />

          <h2>Fractional against full-time</h2>
          <p>
            A full-time COO in Australia is commonly put at $300,000 or more a year once salary, superannuation and incentives are counted, which recruitment and fractional-executive guides consistently cite as the reason the fractional model exists. Ajility&apos;s typical composed retainer starts from $46,800 a year plus GST for businesses under $15M, with no on-costs, no equity, and a quarterly exit. The full-time hire becomes the right answer when the operating layer needs a permanent daily presence, and a fractional engagement done well is usually what makes that hire safe, because the structure the new COO inherits already works.
          </p>

          <h2>What decides your number</h2>
          <p>
            Two questions set the price before any call: how often the operating rhythm runs (fortnightly or weekly), and how many parts of the business need active leadership rather than an installed system the team runs themselves. A business that needs its meeting cadence held and one service line led sits at the bottom of the range. A business where pricing, planning and team structure all need rebuilding at once sits at the top, and usually should start with the Diagnostic instead, because the Diagnostic is how you find out which of those actually binds.
          </p>
        </article>
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-4 sm:px-8">
        <ToolCallout tool="fractional-coo-cost-calculator" articleSlug={SLUG} className="mx-auto max-w-[760px]" />
      </section>

      {/* FAQ */}
      <section className="bg-background py-24">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <span className="eyebrow">FAQ</span>
          <h2 className="mt-4 font-serif text-3xl text-foreground sm:text-4xl">Common questions</h2>
          <dl className="mt-10 space-y-6">
            {faqLd.map((f, index) => (
              <div key={f.q} className="card-panel">
                <dt>
                  <h3 className="font-serif text-lg text-foreground">{f.q}</h3>
                </dt>
                <dd className="mt-3 text-sm leading-relaxed text-charcoal">{faqs[index]?.a ?? f.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-midnight-deep">
        <div className="mx-auto max-w-4xl px-5 py-20 text-center text-frost sm:px-8">
          <h2 className="font-serif text-4xl leading-tight sm:text-5xl">
            Want to know where your real bottleneck is?
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-frost/75">
            Take the free Operating Scan, then book a 30-minute fit call. It is a structured conversation, not a pitch, about where the constraint actually sits in your business.
          </p>
          <div className="mt-10 flex flex-col items-start justify-center gap-3 sm:flex-row">
            <Link
              to="/scan"
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-frost px-7 text-sm font-semibold text-midnight-deep transition hover:bg-white"
            >
              Take the free Operating Scan
              <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
            </Link>
            <BookFitCall
              placement={`insights_article_${SLUG}_footer`}
              buttonClassName="inline-flex min-h-12 items-center justify-center rounded-full border border-frost/40 px-7 text-sm font-semibold text-frost transition hover:border-frost hover:bg-white/10"
              noteClassName="mt-2 text-sm font-medium text-frost/70 hover:text-frost"
            >
              Book a fit call
            </BookFitCall>
          </div>
          <p className="mt-8 font-serif italic text-frost/80">
            &quot;Strategy without execution is hallucination.&quot;
          </p>
        </div>
      </section>
    </div>
  );
}
